import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingAnswersQuery } from 'shared/hooks/queries/use-onboarding-answers-query';
import { useUpdateOnboardingAnswersMutation } from 'shared/hooks/queries/use-update-onboarding-answers-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes';
import {
  ActionEvents,
  OnboardingHearAboutUs,
  OnboardingWorkSituations,
} from 'shared/services/tracking';
import { ID } from 'shared/types/id';
import {
  OnboardingGoal,
  OnboardingSteps,
} from 'shared/types/onboarding-answers';
import { MinimalNewTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { initialWeekStartsOn } from 'shared/types/week-days';
import { getLocalizedStringValue } from 'shared/utils/get-localized-string-value';

import { lifeAreaIds, lifeAreas } from './constants';
import { Goals } from './goals';
import { Habits } from './habits';
import { HearAbout } from './hear-about';
import { LifeAreas } from './life-areas';
import { Tasks } from './tasks';
import { Welcome } from './welcome';
import { WorkSituation } from './work-situation';

const screenOrder = [
  OnboardingSteps.Welcome,
  OnboardingSteps.ToggleLifeAreas,
  OnboardingSteps.CreateGoals,
  OnboardingSteps.CreateHabits,
  OnboardingSteps.CreateTasks,
  OnboardingSteps.WorkSituation,
  OnboardingSteps.HearAbout,
];

export type OnboardingQuestionsProps = {
  onFinish: () => void;
};

export const OnboardingQuestions: React.FC<OnboardingQuestionsProps> = ({
  onFinish,
}) => {
  const locale = useI18nLocale();
  const { data: answers } = useOnboardingAnswersQuery();
  const navigate = useNavigate();

  const activeLifeAreaIds = useMemo(
    () => lifeAreaIds.filter((id) => !answers?.archivedLifeAreas?.includes(id)),
    [answers?.archivedLifeAreas],
  );
  const activeLifeAreas = useMemo(
    () =>
      lifeAreas
        .filter(({ id }) => !answers?.archivedLifeAreas?.includes(id))
        .map(({ name, ...rest }) => ({
          name: getLocalizedStringValue(name, locale),
          ...rest,
        })),
    [answers?.archivedLifeAreas, locale],
  );

  const { mutate: updateAnswers } = useUpdateOnboardingAnswersMutation();

  const [currentScreen, setCurrentScreen] = useState<OnboardingSteps>(
    answers?.lastShownStep ?? OnboardingSteps.Welcome,
  );
  const updateOnboardingScreen = (screen: OnboardingSteps) => {
    const newIndex = screenOrder.indexOf(screen);
    if (
      !answers?.lastShownStep ||
      newIndex > screenOrder.indexOf(answers?.lastShownStep)
    ) {
      updateAnswers({ lastShownStep: screen });
    }
  };
  const nextStep = () => {
    const idx = screenOrder.indexOf(currentScreen);
    if (idx < screenOrder.length - 1) {
      setCurrentScreen(screenOrder[idx + 1]);
      updateOnboardingScreen(screenOrder[idx + 1]);
      return;
    }
    onFinish();
  };
  const prevStep = () => {
    const idx = screenOrder.indexOf(currentScreen);
    if (idx > 0) {
      setCurrentScreen(screenOrder[idx - 1]);
      return;
    }

    // Update the route to login when the start is reached and the user wants to go back
    navigate(Paths.Login);
  };

  const track = useTrackEvents();
  const onSubmitLifeAreas = (selectedIds: ID[]) => {
    updateAnswers({
      archivedLifeAreas: lifeAreaIds.filter((id) => !selectedIds?.includes(id)),
    });

    track(ActionEvents.OnboardingLifeAreasSubmit, {
      answer: selectedIds,
    });
  };

  const onSubmitGoals = async (goals: OnboardingGoal[]) => {
    updateAnswers({ goals });
    track(ActionEvents.OnboardingGoalsSubmit, { answer: goals.length });
  };

  const onSubmitTasks = async (tasks: MinimalNewTask[]) => {
    const isHabits = tasks.some(({ type }) => !!type && type !== TaskType.Task);
    if (isHabits) {
      updateAnswers({ habits: tasks });
      track(ActionEvents.OnboardingHabitsSubmit, { answer: tasks.length });
      return;
    }

    updateAnswers({ tasks });
    track(ActionEvents.OnboardingTasksSubmit, { answer: tasks.length });
  };

  const onSubmitWorkSituation = (workSituation: OnboardingWorkSituations) => {
    updateAnswers({ workSituation });
    track(ActionEvents.OnboardingWorkSituationSubmit, {
      answer: workSituation,
    });
  };
  const onSubmitHearAbout = (hearAbout: OnboardingHearAboutUs) => {
    updateAnswers({ hearAbout });
    track(ActionEvents.OnboardingHearAboutSubmit, { answer: hearAbout });
  };

  const prevScreenRef = useRef<OnboardingSteps>(currentScreen);
  useEffect(() => {
    prevScreenRef.current = currentScreen;
  }, [currentScreen]);

  const TOTAL_ONBOARDING_STEPS = screenOrder.length + 1;
  const currentIndex = screenOrder.indexOf(currentScreen) + 1;
  const prevIndex = screenOrder.indexOf(prevScreenRef.current) + 1;

  let ScreenComponent;
  switch (currentScreen) {
    case OnboardingSteps.Welcome:
      ScreenComponent = (
        <Welcome
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
        />
      );
      break;
    case OnboardingSteps.ToggleLifeAreas:
      ScreenComponent = (
        <LifeAreas
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          selectedLifeAreaTemplateIds={activeLifeAreaIds}
          onSubmit={onSubmitLifeAreas}
        />
      );
      break;
    case OnboardingSteps.CreateGoals:
      ScreenComponent = (
        <Goals
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          availableLifeAreas={activeLifeAreas}
          onSubmit={onSubmitGoals}
        />
      );
      break;
    case OnboardingSteps.CreateHabits:
      ScreenComponent = (
        <Habits
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          weekStartsOn={initialWeekStartsOn}
          lifeAreas={activeLifeAreas}
          goals={answers?.goals ?? []}
          onSubmit={onSubmitTasks}
        />
      );
      break;
    case OnboardingSteps.CreateTasks:
      ScreenComponent = (
        <Tasks
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          lifeAreas={activeLifeAreas}
          goals={answers?.goals ?? []}
          onSubmit={onSubmitTasks}
        />
      );
      break;
    case OnboardingSteps.WorkSituation:
      ScreenComponent = (
        <WorkSituation
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          onSubmit={onSubmitWorkSituation}
        />
      );
      break;
    case OnboardingSteps.HearAbout:
      ScreenComponent = (
        <HearAbout
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentIndex + 1}
          previousStep={prevIndex + 1}
          totalSteps={TOTAL_ONBOARDING_STEPS}
          onSubmit={onSubmitHearAbout}
        />
      );
      break;
    default:
      ScreenComponent = null;
  }

  return ScreenComponent;
};
